/**
 * Centralized annotation sync queue that persists across image changes.
 *
 * This service manages a queue of annotation sync requests, ensuring:
 * 1. Syncs continue in background even when user navigates to different images
 * 2. Multiple syncs for the same image are deduplicated (only latest runs)
 * 3. Approve Image can await all pending syncs for a source before completing
 * 4. User experience feels instant - no waiting for sync to complete
 */

import type { Annotation } from "@rapid/types/annotation";
import type { AiToolUsed, AnnotationData } from "@rapid/utils/api/datasets";

export interface SyncRequest {
    sourceId: string;
    annotations: Annotation[];
    annotationData: AnnotationData;
    datasetId: string;
    annotationGroup: string;
    nextAnnotationId: string;
    aiToolUsed: AiToolUsed;
    timestamp: number;
}

export interface PendingSync {
    request: SyncRequest;
    promise: Promise<void>;
    status: "pending" | "in_progress" | "completed" | "failed";
    error?: Error;
}

type SyncExecutor = (request: SyncRequest) => Promise<void>;

class AnnotationSyncQueue {
    // Map of sourceId -> pending sync (only keep latest per source)
    private pendingSyncs: Map<string, PendingSync> = new Map();

    // The function that actually performs the sync (injected from useAnnotationSync)
    private syncExecutor: SyncExecutor | null = null;

    // Debounce timers per source
    private debounceTimers: Map<string, NodeJS.Timeout> = new Map();

    // Pending requests waiting for debounce (stored so we can flush them)
    private pendingRequests: Map<string, SyncRequest> = new Map();

    // Debounce delay in ms
    private readonly debounceMs = 600;

    /**
     * Set the sync executor function (called from useAnnotationSync hook)
     */
    setSyncExecutor(executor: SyncExecutor): void {
        this.syncExecutor = executor;
    }

    /**
     * Queue an annotation sync for a source.
     * If there's already a pending sync for this source, it will be replaced.
     * The sync is debounced to avoid rapid-fire requests.
     */
    queueSync(request: SyncRequest): void {
        const { sourceId } = request;

        // Clear existing debounce timer for this source
        const existingTimer = this.debounceTimers.get(sourceId);
        if (existingTimer) {
            clearTimeout(existingTimer);
        }

        // Store the pending request so we can flush it if needed
        this.pendingRequests.set(sourceId, request);

        // Set new debounce timer
        const timer = setTimeout(() => {
            this.flushPendingRequest(sourceId);
        }, this.debounceMs);

        this.debounceTimers.set(sourceId, timer);

        console.log(`[SyncQueue] Queued sync for source ${sourceId} (debounced)`);
    }

    /**
     * Flush a pending debounced request immediately (execute it now)
     */
    private flushPendingRequest(sourceId: string): void {
        const request = this.pendingRequests.get(sourceId);
        if (request) {
            this.pendingRequests.delete(sourceId);
            this.debounceTimers.delete(sourceId);
            this.executeSync(request);
        }
    }

    /**
     * Execute a sync request immediately (after debounce)
     */
    private async executeSync(request: SyncRequest): Promise<void> {
        const { sourceId } = request;

        if (!this.syncExecutor) {
            console.error("[SyncQueue] No sync executor set");
            return;
        }

        // Create the sync promise
        const syncPromise = (async () => {
            const pendingSync = this.pendingSyncs.get(sourceId);
            if (pendingSync) {
                pendingSync.status = "in_progress";
            }

            try {
                console.log(`[SyncQueue] Starting sync for source ${sourceId}`);
                await this.syncExecutor!(request);
                console.log(`[SyncQueue] Completed sync for source ${sourceId}`);

                if (pendingSync) {
                    pendingSync.status = "completed";
                }
            } catch (error) {
                console.error(`[SyncQueue] Failed sync for source ${sourceId}:`, error);

                if (pendingSync) {
                    pendingSync.status = "failed";
                    pendingSync.error = error instanceof Error ? error : new Error(String(error));
                }
                // Don't rethrow - we want to handle errors gracefully
            } finally {
                // Clean up completed/failed syncs after a delay
                setTimeout(() => {
                    const current = this.pendingSyncs.get(sourceId);
                    if (current?.status === "completed" || current?.status === "failed") {
                        this.pendingSyncs.delete(sourceId);
                    }
                }, 5000);
            }
        })();

        // Store the pending sync
        this.pendingSyncs.set(sourceId, { request, promise: syncPromise, status: "pending" });
    }

    /**
     * Wait for all pending syncs for a specific source to complete.
     * Used by Approve Image to ensure annotations are saved before approval.
     * This will flush any debounced request immediately before awaiting.
     */
    async awaitSyncsForSource(sourceId: string): Promise<void> {
        // First, flush any pending debounced sync (execute it immediately)
        const timer = this.debounceTimers.get(sourceId);
        if (timer) {
            clearTimeout(timer);
            this.flushPendingRequest(sourceId);
        }

        const pendingSync = this.pendingSyncs.get(sourceId);
        if (!pendingSync) {
            console.log(`[SyncQueue] No pending sync for source ${sourceId}`);
            return;
        }

        console.log(`[SyncQueue] Awaiting sync for source ${sourceId}`);
        await pendingSync.promise;

        if (pendingSync.status === "failed" && pendingSync.error) {
            throw pendingSync.error;
        }
    }

    /**
     * Wait for all pending syncs to complete.
     * Useful for cleanup or ensuring all data is saved.
     */
    async awaitAllSyncs(): Promise<void> {
        // Flush all debounced syncs first
        for (const [sourceId, timer] of this.debounceTimers.entries()) {
            clearTimeout(timer);
            this.debounceTimers.delete(sourceId);
        }

        const promises = Array.from(this.pendingSyncs.values()).map((sync) => sync.promise);
        console.log(`[SyncQueue] Awaiting ${promises.length} pending syncs`);
        await Promise.all(promises);
    }

    /**
     * Check if there are any pending syncs for a source
     */
    hasPendingSync(sourceId: string): boolean {
        return (
            this.debounceTimers.has(sourceId) ||
            (this.pendingSyncs.has(sourceId) &&
                this.pendingSyncs.get(sourceId)?.status !== "completed")
        );
    }

    /**
     * Get the status of a pending sync for a source
     */
    getSyncStatus(sourceId: string): PendingSync["status"] | null {
        const pending = this.pendingSyncs.get(sourceId);
        if (!pending) {
            return this.debounceTimers.has(sourceId) ? "pending" : null;
        }
        return pending.status;
    }

    /**
     * Cancel pending sync for a source (e.g., if image is removed)
     */
    cancelSync(sourceId: string): void {
        const timer = this.debounceTimers.get(sourceId);
        if (timer) {
            clearTimeout(timer);
            this.debounceTimers.delete(sourceId);
        }
        // Note: We don't cancel in-progress syncs, only debounced ones
        console.log(`[SyncQueue] Cancelled pending sync for source ${sourceId}`);
    }

    /**
     * Clear all pending syncs (e.g., on logout or navigation away)
     */
    clear(): void {
        for (const timer of this.debounceTimers.values()) {
            clearTimeout(timer);
        }
        this.debounceTimers.clear();
        this.pendingSyncs.clear();
        console.log("[SyncQueue] Cleared all syncs");
    }
}

// Export singleton instance
export const annotationSyncQueue = new AnnotationSyncQueue();
